import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { SystemSettingService } from '@app/api/systemsettting.service';
import { environment } from 'environments/environment';
import { ModalEditAboutUsItemComponent } from './modal-edit-about-us-item/modal-edit-about-us-item.component';

export interface SystemSettingItem {
  id?: string;
  parent_id?: string;
  type?: string;
  name?: string;
  value?: string;
  description?: string;
  sort_order?: number;
  is_hidden?: boolean;
  page_location?: string;
  slug?: string;
  language?: string;
}

export interface SectionGroup {
  id?: string;
  parent_title: string;
  type: string;
  is_hidden: boolean;
  sort_order: number;
  page_location: string;
  language: string;
  children: SystemSettingItem[];
}

@Component({
  selector: 'app-about-us-management',
  templateUrl: './about-us-management.component.html',
  styleUrls: ['./about-us-management.component.scss'],
})
export class AppAboutUsManagementComponent implements OnInit {
  selectedLang: string = 'vi';
  isLoading = false;
  host = environment.backendhost;

  companyInfoSection: SectionGroup = {
    parent_title: 'Về chúng tôi (Thông tin công ty)',
    type: 'company_info',
    is_hidden: false,
    sort_order: 1,
    page_location: 'about_us',
    language: 'vi',
    children: [],
  };

  visionMissionSection: SectionGroup = {
    parent_title: 'Tầm nhìn & Sứ mệnh',
    type: 'vision_mission',
    is_hidden: false,
    sort_order: 2,
    page_location: 'about_us',
    language: 'vi',
    children: [],
  };

  contactCtaSection: SectionGroup = {
    parent_title: 'Khối liên hệ CTA',
    type: 'contact_cta',
    is_hidden: false,
    sort_order: 3,
    page_location: 'about_us',
    language: 'vi',
    children: [],
  };

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
    private systemService: SystemSettingService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadData(this.selectedLang);
  }

  onTabChange(event: any): void {
    const tabIndex = event.index;
    if (tabIndex === 0) {
      this.selectedLang = 'vi';
    } else if (tabIndex === 1) {
      this.selectedLang = 'en';
    } else if (tabIndex === 2) {
      this.selectedLang = 'ch';
    }
    this.loadData(this.selectedLang);
  }

  resetSections(lang: string): void {
    this.companyInfoSection = {
      parent_title: 'Về chúng tôi (Thông tin công ty)',
      type: 'company_info',
      is_hidden: false,
      sort_order: 1,
      page_location: 'about_us',
      language: lang,
      children: [],
    };

    this.visionMissionSection = {
      parent_title: 'Tầm nhìn & Sứ mệnh',
      type: 'vision_mission',
      is_hidden: false,
      sort_order: 2,
      page_location: 'about_us',
      language: lang,
      children: [],
    };

    this.contactCtaSection = {
      parent_title: 'Khối liên hệ CTA',
      type: 'contact_cta',
      is_hidden: false,
      sort_order: 3,
      page_location: 'about_us',
      language: lang,
      children: [],
    };
  }

  loadData(lang: string): void {
    this.resetSections(lang);
    this.isLoading = true;
    const filter = 'page_location==about_us,language==' + lang;

    this.systemService.apiSystemSettingGet(filter, 'sort_order', 1, 50).subscribe({
      next: (res) => {
        let collection: any[] = [];
        if (res && res.success && res.data) {
          if (Array.isArray(res.data.collection)) {
            collection = res.data.collection;
          } else if (Array.isArray(res.data)) {
            collection = res.data;
          }
        }

        // Fallback between 'ch' and 'zh' if no records returned
        if (collection.length === 0 && (lang === 'ch' || lang === 'zh')) {
          const fallbackLang = lang === 'ch' ? 'zh' : 'ch';
          const fallbackFilter = 'page_location==about_us,language==' + fallbackLang;
          this.systemService.apiSystemSettingGet(fallbackFilter, 'sort_order', 1, 50).subscribe({
            next: (fbRes) => {
              if (fbRes && fbRes.success && fbRes.data) {
                if (Array.isArray(fbRes.data.collection)) {
                  collection = fbRes.data.collection;
                } else if (Array.isArray(fbRes.data)) {
                  collection = fbRes.data;
                }
              }
              this.applyCollection(collection, lang);
            },
            error: () => {
              this.applyCollection(collection, lang);
            },
          });
          return;
        }

        this.applyCollection(collection, lang);
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error('Lỗi khi tải cấu hình About Us: ' + (err && err.message ? err.message : ''));
        this.cdr.detectChanges();
      },
    });
  }

  private applyCollection(collection: any[], lang: string): void {
    this.companyInfoSection = this.parseSectionData(collection, 'company_info', 'Về chúng tôi (Thông tin công ty)', 1, lang);
    this.visionMissionSection = this.parseSectionData(collection, 'vision_mission', 'Tầm nhìn & Sứ mệnh', 2, lang);
    this.contactCtaSection = this.parseSectionData(collection, 'contact_cta', 'Khối liên hệ CTA', 3, lang);

    // If section parent has not been created yet in DB, ensure it in background
    if (!this.companyInfoSection.id) {
      this.ensureParentSection('company_info', 'Về chúng tôi', 1, lang);
    }
    if (!this.visionMissionSection.id) {
      this.ensureParentSection('vision_mission', 'Tầm nhìn & Sứ mệnh', 2, lang);
    }
    if (!this.contactCtaSection.id) {
      this.ensureParentSection('contact_cta', 'Liên hệ với chúng tôi', 3, lang);
    }

    this.isLoading = false;
    this.cdr.detectChanges();
  }

  private parseSectionData(collection: any[], typeName: string, defaultTitle: string, defaultSort: number, lang: string): SectionGroup {
    const targetType = typeName.toLowerCase();

    // 1. Locate parent item
    const parent =
      collection.find(
        (s) => s.type && s.type.toLowerCase() === targetType && (!s.parent_id || s.parent_id === '' || s.parent_id === null)
      ) || collection.find((s) => s.type && s.type.toLowerCase() === targetType);

    const childrenList: SystemSettingItem[] = [];

    if (parent) {
      // 2. Extract nested children array if exists
      if (parent.children && Array.isArray(parent.children) && parent.children.length > 0) {
        parent.children.forEach((c: any) => {
          if (!childrenList.some((item) => item.id === c.id)) {
            childrenList.push(c);
          }
        });
      }

      // 3. Extract flat child rows in collection
      collection.forEach((item) => {
        if (item.id !== parent.id) {
          const isChildOfThisParent = item.parent_id === parent.id;
          const isChildOfThisType = item.type && item.type.toLowerCase() === targetType && item.parent_id;
          if (isChildOfThisParent || isChildOfThisType) {
            if (!childrenList.some((c) => c.id === item.id)) {
              childrenList.push(item);
            }
          }
        }
      });

      childrenList.sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));

      return {
        id: parent.id,
        parent_title: parent.parent_title || defaultTitle,
        type: parent.type || typeName,
        is_hidden: parent.is_hidden || false,
        sort_order: parent.sort_order != null ? parent.sort_order : defaultSort,
        page_location: parent.page_location || 'about_us',
        language: parent.language || lang,
        children: childrenList,
      };
    }

    // 4. If no parent found, gather any child matching this type
    collection.forEach((item) => {
      if (item.type && item.type.toLowerCase() === targetType) {
        if (!childrenList.some((c) => c.id === item.id)) {
          childrenList.push(item);
        }
      }
    });

    if (childrenList.length > 0) {
      childrenList.sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));
    }

    return {
      id: '',
      parent_title: defaultTitle,
      type: typeName,
      is_hidden: false,
      sort_order: defaultSort,
      page_location: 'about_us',
      language: lang,
      children: childrenList,
    };
  }

  private ensureParentSection(type: string, title: string, sortOrder: number, lang: string): void {
    const parentObj = {
      parent_title: title,
      type: type,
      page_location: 'about_us',
      is_hidden: false,
      sort_order: sortOrder,
      language: lang,
    };

    this.systemService.apiSystemSettingPost(parentObj).subscribe({
      next: (res) => {
        if (res && res.success && res.data) {
          const newId = res.data.id;
          if (type === 'company_info' && !this.companyInfoSection.id) this.companyInfoSection.id = newId;
          else if (type === 'vision_mission' && !this.visionMissionSection.id) this.visionMissionSection.id = newId;
          else if (type === 'contact_cta' && !this.contactCtaSection.id) this.contactCtaSection.id = newId;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        console.error('Error creating parent section', type, err);
      },
    });
  }

  openAddItemModal(section: SectionGroup): void {
    if (!section.id) {
      this.toastr.warning('Đang khởi tạo nhóm cha, vui lòng thử lại sau 1 giây...');
      return;
    }

    const isVm = (section.type || '').toLowerCase() === 'vision_mission';
    const dialogRef = this.dialog.open(ModalEditAboutUsItemComponent, {
      width: isVm ? '750px' : '600px',
      maxWidth: '95vw',
      data: {
        isNew: true,
        parentId: section.id,
        sectionType: section.type,
        sectionTitle: section.parent_title,
        lang: this.selectedLang,
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res && res.success) {
        this.loadData(this.selectedLang);
      }
    });
  }

  openEditItemModal(item: SystemSettingItem, section: SectionGroup): void {
    const isVm = (section.type || item.type || '').toLowerCase() === 'vision_mission';
    const dialogRef = this.dialog.open(ModalEditAboutUsItemComponent, {
      width: isVm ? '750px' : '600px',
      maxWidth: '95vw',
      data: {
        isNew: false,
        parentId: section.id || item.parent_id || '',
        sectionType: section.type || item.type || '',
        sectionTitle: section.parent_title,
        lang: this.selectedLang,
        item: { ...item },
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res && res.success) {
        this.loadData(this.selectedLang);
      }
    });
  }

  toggleHideItem(item: SystemSettingItem, section: SectionGroup): void {
    const updatedStatus = !item.is_hidden;
    const payload: any = {
      ...item,
      id: item.id,
      parent_id: section.id || item.parent_id,
      type: section.type || item.type,
      page_location: 'about_us',
      language: this.selectedLang,
      is_hidden: updatedStatus,
    };

    this.systemService.apiSystemSettingIdPut(item.id, payload).subscribe({
      next: (res) => {
        if (res && res.success) {
          item.is_hidden = updatedStatus;
          this.toastr.success(updatedStatus ? 'Đã ẩn thuộc tính' : 'Đã hiển thị thuộc tính');
          this.cdr.detectChanges();
        } else {
          this.toastr.error('Thao tác thất bại');
        }
      },
      error: () => {
        this.toastr.error('Lỗi khi cập nhật trạng thái');
      },
    });
  }

  deleteItem(item: SystemSettingItem): void {
    if (!item.id) return;
    const itemName = item.description || item.name || '';
    if (confirm('Bạn có chắc chắn muốn xóa thuộc tính "' + itemName + '"?')) {
      this.systemService.apiSystemSettingIdDelete(item.id).subscribe({
        next: (res) => {
          if (res && res.success) {
            this.toastr.success('Xóa thuộc tính thành công');
            this.loadData(this.selectedLang);
          } else {
            this.toastr.error('Xóa thất bại');
          }
        },
        error: (err) => {
          this.toastr.error('Lỗi khi xóa thuộc tính: ' + (err && err.message ? err.message : ''));
        },
      });
    }
  }

  isImage(name?: string, value?: string): boolean {
    const fieldName = (name || '').toLowerCase();
    if (
      fieldName.indexOf('logo') !== -1 ||
      fieldName.indexOf('image') !== -1 ||
      fieldName.indexOf('background') !== -1 ||
      fieldName.indexOf('img') !== -1 ||
      fieldName.indexOf('banner') !== -1 ||
      fieldName.indexOf('avatar') !== -1 ||
      fieldName.indexOf('icon') !== -1
    ) {
      return true;
    }

    const val = (value || '').trim().toLowerCase();
    if (!val) return false;
    if (val.indexOf('/public/') === 0 || val.indexOf('public/') === 0) return true;
    if (val.indexOf('http://') === 0 || val.indexOf('https://') === 0 || val.indexOf('data:image/') === 0) {
      if (val.match(/\.(png|jpe?g|gif|webp|svg|ico)(\?.*)?$/i)) return true;
    }
    return false;
  }

  getImageUrl(path?: string): string {
    if (!path) return '';
    const trimmed = path.trim();
    if (
      trimmed.indexOf('http://') === 0 ||
      trimmed.indexOf('https://') === 0 ||
      trimmed.indexOf('data:') === 0 ||
      trimmed.indexOf('blob:') === 0
    ) {
      return trimmed;
    }
    if (trimmed.indexOf('./assets/') === 0 || trimmed.indexOf('assets/') === 0) {
      return trimmed;
    }
    const host = (this.host || '').replace(/\/+$/, '');
    const cleanPath = trimmed.indexOf('/') === 0 ? trimmed : '/' + trimmed;
    return host + cleanPath;
  }
}
